import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Eye, 
  EyeOff, 
  LogOut, 
  Loader2,
  FileText,
  Calendar,
  Tag
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { fetchAllBlogs, deleteBlogPost, togglePublishStatus } from '../lib/firebaseBlog';

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadPosts();
    }
  }, [isAuthenticated]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const data = await fetchAllBlogs();
      setPosts(data);
    } catch (err) {
      console.error('Error loading posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  const handleDelete = async (id) => {
    setActionLoading(id);
    try {
      await deleteBlogPost(id);
      setPosts(posts.filter(p => p.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting post:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleTogglePublish = async (id, currentStatus) => {
    setActionLoading(id);
    try {
      await togglePublishStatus(id, currentStatus);
      setPosts(posts.map(p => 
        p.id === id ? { ...p, published: !currentStatus } : p
      ));
    } catch (err) {
      console.error('Error toggling publish status:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-heading text-2xl font-bold text-primary">
              SK
            </Link>
            <span className="text-muted-foreground font-body">|</span>
            <h1 className="font-heading text-xl font-semibold text-foreground">
              Blog Admin
            </h1>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="font-body text-sm text-muted-foreground hidden sm:block">
              {user?.email}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground font-body text-sm rounded-lg hover:bg-muted/80 transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats & Actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              <span className="font-body text-foreground">
                <span className="font-semibold">{posts.length}</span> Posts
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-accent" />
              <span className="font-body text-foreground">
                <span className="font-semibold">{posts.filter(p => p.published).length}</span> Published
              </span>
            </div>
            <div className="flex items-center gap-2">
              <EyeOff className="w-5 h-5 text-muted-foreground" />
              <span className="font-body text-foreground">
                <span className="font-semibold">{posts.filter(p => !p.published).length}</span> Drafts
              </span>
            </div>
          </div>

          <Link
            to="/admin/posts/new"
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground font-body font-medium rounded-lg hover:bg-primary/90 transition-colors"
            data-testid="create-post-button"
          >
            <Plus className="w-5 h-5" />
            New Post
          </Link>
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : posts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20 bg-card border border-border rounded-lg"
          >
            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
              No posts yet
            </h2>
            <p className="font-body text-muted-foreground mb-6">
              Create your first blog post to get started.
            </p>
            <Link
              to="/admin/posts/new"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground font-body font-medium rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Post
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {posts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-card border border-border rounded-lg p-6 hover:border-primary/30 transition-colors"
                data-testid={`admin-post-${post.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2.5 py-1 text-xs font-body font-medium rounded-full ${
                        post.published 
                          ? 'bg-accent/20 text-accent' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                      <span className="px-2.5 py-1 text-xs font-body font-medium rounded-full bg-primary/10 text-primary capitalize">
                        {post.category === 'rabbit_hole' ? 'Rabbit Hole' : post.category}
                      </span>
                    </div>
                    
                    <h3 className="font-heading text-xl font-semibold text-foreground mb-2 truncate">
                      {post.title || 'Untitled'}
                    </h3>
                    
                    <p className="font-body text-sm text-muted-foreground line-clamp-2 mb-3">
                      {post.excerpt || 'No excerpt'}
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-4 text-xs font-body text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(post.created_at)}
                      </span>
                      {post.tags && post.tags.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Tag className="w-3.5 h-3.5" />
                          {post.tags.slice(0, 3).join(', ')}
                          {post.tags.length > 3 && ` +${post.tags.length - 3}`}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Link
                      to={`/blog/${post.id}`}
                      target="_blank"
                      className="p-2.5 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                      title="Preview"
                      data-testid={`preview-post-${post.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Link>
                    
                    <button
                      onClick={() => handleTogglePublish(post.id, post.published)}
                      disabled={actionLoading === post.id}
                      className={`p-2.5 rounded-lg transition-colors ${
                        post.published
                          ? 'bg-accent/20 text-accent hover:bg-accent/30'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      title={post.published ? 'Unpublish' : 'Publish'}
                      data-testid={`toggle-publish-${post.id}`}
                    >
                      {actionLoading === post.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : post.published ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>

                    <Link
                      to={`/admin/posts/${post.id}`}
                      className="p-2.5 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
                      title="Edit"
                      data-testid={`edit-post-${post.id}`}
                    >
                      <Edit3 className="w-4 h-4" />
                    </Link>

                    <button
                      onClick={() => setDeleteConfirm(post.id)}
                      className="p-2.5 bg-destructive/10 text-destructive rounded-lg hover:bg-destructive/20 transition-colors"
                      title="Delete"
                      data-testid={`delete-post-${post.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Delete Confirmation */}
                {deleteConfirm === post.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 pt-4 border-t border-border"
                  >
                    <p className="font-body text-sm text-foreground mb-3">
                      Are you sure you want to delete this post? This action cannot be undone.
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDelete(post.id)}
                        disabled={actionLoading === post.id}
                        className="px-4 py-2 bg-destructive text-destructive-foreground font-body text-sm rounded-lg hover:bg-destructive/90 transition-colors flex items-center gap-2"
                        data-testid={`confirm-delete-${post.id}`}
                      >
                        {actionLoading === post.id ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          'Yes, Delete'
                        )}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-4 py-2 bg-muted text-foreground font-body text-sm rounded-lg hover:bg-muted/80 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
